import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import ThumbUpRounded from "@mui/icons-material/ThumbUpRounded";

/**
 * Inpatient Daily Plan: a comment and a "reassuring" mark for one checked item, entered the same
 * way the Tests step enters a result (the card's pencil opens this dialog). A checked item with
 * neither is listed as pending in the note.
 */
const DailyPlanNoteDialog = ({ open, item, comment = "", reassuring = false, onClose, onExited, onSave }) => {
  const { control, handleSubmit, reset } = useForm({
    defaultValues: { comment, reassuring },
  });

  useEffect(() => {
    if (open) reset({ comment, reassuring });
  }, [open, comment, reassuring, reset]);

  const save = handleSubmit((values) => {
    onSave(item.id, { comment: values.comment, reassuring: values.reassuring });
    onClose();
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth TransitionProps={{ onExited }}>
      <DialogTitle>{item?.name}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 3 }}>
          Add a note or mark as reassuring for quick documentation. Items with neither are listed
          as pending.
        </DialogContentText>

        {/* Same control as the Tests step's result dialog (TestResultModal). Here it toggles
            rather than saving, because a Daily Plan item can be reassuring and have a note. */}
        <Controller
          name="reassuring"
          control={control}
          render={({ field }) => (
            <Button
              fullWidth
              variant={field.value ? "contained" : "outlined"}
              startIcon={<ThumbUpRounded />}
              onClick={() => field.onChange(!field.value)}
              aria-pressed={field.value}
            >
              {field.value ? "Marked as Reassuring" : "Mark as Reassuring"}
            </Button>
          )}
        />

        <Controller
          name="comment"
          control={control}
          render={({ field }) => (
            <TextField {...field} label="Note" multiline minRows={3} fullWidth autoFocus sx={{ mt: 2.5 }} />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DailyPlanNoteDialog;
